import NIMType from '@yxim/nim-web-sdk/dist/types/nim/index'
import type { NIMMessage } from '@yxim/nim-web-sdk/dist/types/nim/MessageInterface'
import type { NIMSession } from '@yxim/nim-web-sdk/dist/types/nim/SessionInterface'
import type { NIMUserNameCard } from '@yxim/nim-web-sdk/dist/types/nim/UserInterface'
import type { myMsgType } from '@/types/viewsType/chatType'
import { apiTranslate, apiGetConfigure } from '@/api/chat'
import type { configureInterface } from '@/types/apiType/chatType'

export const useChatStore = defineStore({
  id: 'chat',
  state: () => ({
    nim: {} as NIMType, //nim实例
    sessionlist: [] as NIMSession[], //会话列表
    sessionMap: {} as Record<string, NIMSession>, //会话列表{p2p-id:{}}
    currSessionMsgs: [] as myMsgType[], //当前会话消息
    currSessionLastMsg: {} as NIMMessage, //当前会话最后一条消息
    msgs: {}, //消息{用户id:[]}
    currSessionId: '', //当前会话id
    myInfo: {} as NIMUserNameCard, //我的信息
    userInfoList: [] as NIMUserNameCard[], //用户信息数组
    userInfos: {} as Record<string, NIMUserNameCard>, //用户信息
    finished: false, //历史记录加载完了
    limit: 20, //一次20条消息
    allUnread: 0, //会话总未读数
    showSkeleton: true, // 是否显示骨架屏 收到会话列表同步完成后 骨架屏取消
    translateTexts: {} as { [prop: string]: string }, //翻译的聊天信息{ idClient: '翻译的文本'}  idClient是IM会话的唯一ID
    IM_SERVER_ID: useRuntimeConfig().public.imServerId, // 服务号ID
    chatConfigure: {} as configureInterface // 会话限制 限制等级 5
  }),
  getters: {
    // 当前会话uid
    currSessionUid(state) {
      return state.currSessionId.replace('p2p-', '')
    }
  },
  actions: {
    // 重置翻译聊天字段，切换语言时调用
    resetTranslateTexts() {
      this.translateTexts = {}
    },
    // 更新翻译聊天信息，如果缓存里已有就return
    async updateTranslateTexts(idClient: string, text: string) {
      const res = await apiTranslate({ text })
      const { code, data } = res
      if (code === 200) {
        this.translateTexts[idClient] = data || ''
      }
    },
    // 获取会话限制消息
    async getChatConfigure() {
      const res = await apiGetConfigure()
      const { code, data } = res
      if (code === 200) {
        this.chatConfigure = data
      }
    },
    // 初始化nim
    initNIM() {
      const login =
        JSON.parse(localStorage.getItem('loginInfo') as string) || {}
      const { netEaseToken, uid } = login
      const NIM = window.NIM
      const runtimeConfig = useRuntimeConfig()
      this.nim = NIM.getInstance({
        debug: false, // 是否开启日志，将其打印到console。集成开发阶段建议打开。
        appKey: runtimeConfig.public.nimId,
        account: uid, //用于登录IM
        token: netEaseToken, //用于登录IM
        db: false, //若不要开启数据库请设置false。SDK 默认为true。
        syncSessionUnread: true, //同步会话的未读数,多端同步
        onconnect: this.onConnect,
        onwillreconnect: this.onWillReconnect,
        ondisconnect: this.onDisconnect,
        onerror: this.onError,
        //消息收发的监听事件
        // onroamingmsgs: this.onRoamingMsgs,
        // onofflinemsgs: this.onOfflineMsgs,
        onmsg: this.onMsg,
        // 最近会话
        onsessions: this.onSessions,
        onupdatesession: this.onUpdateSession,
        // 用户名片
        onmyinfo: this.onMyInfo,
        onupdatemyinfo: this.onMyInfo,
        onusers: this.onUsers,
        onupdateuser: this.onUpdateUser,
        onsyncdone: this.onSyncdone
      })
    },
    // 连接成功
    onConnect() {
      console.log('IM => 连接成功')
    },
    // 重连
    onWillReconnect(obj) {
      // 此时说明 SDK 已经断开连接, 请开发者在界面上提示用户连接已断开, 而且正在重新建立连接
      console.log('IM => 即将重连', obj.retryCount, obj.duration)
    },
    // 断连
    onDisconnect(error) {
      // 此时说明 SDK 处于断开状态, 开发者此时应该根据错误码提示相应的错误信息, 并且跳转到登录页面
      console.log('IM => 丢失连接', error)
      const publicStore = usePublicStore()
      if (error) {
        switch (error.code) {
          // 账号或者密码错误, 请跳转到登录页面并提示错误
          case 302:
            publicStore.resetLogin({ msg: 'IM-302', type: 'im' })
            break
          // 重复登录, 已经在其它端登录了, 请跳转到登录页面并提示错误
          case 417:
            publicStore.resetLogin({ msg: 'IM-417', type: 'im' })
            break
          // 被踢, 请提示错误后跳转到登录页面
          case 'kicked':
            publicStore.resetLogin({ msg: 'IM-kicked', type: 'im' })
            break
          default:
            break
        }
      }
    },
    // 错误
    onError(error) {
      console.log('IM => error', error)
    },
    // 漫游消息
    // onRoamingMsgs(obj) {
    //   // console.log('收到漫游消息', obj)
    //   this.updateMsgs(obj.msgs)
    // },
    // 离线消息
    // onOfflineMsgs(obj) {
    //   // console.log('收到离线消息', obj)
    //   this.updateMsgs(obj.msgs)
    // },
    // 收到消息
    onMsg(msg) {
      console.log('IM => 收到消息', msg.scene, msg.type, msg)

      this.putMsg(msg)
      if (msg.sessionId === this.currSessionId) {
        this.updateCurrSessionMsgs({
          type: 'put',
          msg
        })
        // 发送已读回执
        this.sendMsgReceipt()
      }

      const callStore = useCallStore()
      callStore.setValidCallAfterUids(msg) // 设置有效通话过的用户uid

      this.filterValidUids([msg]) // 过滤有效uid
    },
    // 收到会话列表
    onSessions(sessions) {
      console.log('IM => 收到会话列表', sessions)
      // 生成用户id数组
      const accounts: string[] = []
      sessions.forEach((item: { to: string }) => {
        accounts.push(item.to)
      })
      // 变成二维数组，150
      const matrixAccounts = common.arrayToMatrix(accounts, 150) as string[]
      // 获取用户资料数组
      this.getUsers(matrixAccounts)

      this.updateSessions(sessions)

      this.filterValidUids(sessions) // 过滤有效uid
    },
    // 会话更新了
    onUpdateSession(session) {
      console.log('IM => 会话更新了', session)
      // 获取用户信息
      if (!this.userInfos[session.to]) {
        this.getUser(session.to)
      }
      this.updateSessions(session)
    },
    // 我的个人信息
    onMyInfo(obj: NIMUserNameCard) {
      this.myInfo = obj
    },
    // 收到用户资料列表
    onUsers(users) {
      console.log('IM => 收到用户资料列表', users)
      this.updateUserInfo(users)
    },
    // 用户资料更新了
    onUpdateUser(user) {
      console.log('IM => 用户资料更新了', user)
      this.updateUserInfo(user)
    },
    // 消息已读
    sendMsgReceipt() {
      // 如果有当前会话
      const currSessionId = this.currSessionId
      if (currSessionId) {
        // 只有点对点消息才发已读回执
        if (common.parseSession(currSessionId)?.scene === 'p2p') {
          const nim = this.nim
          if (this.sessionMap[currSessionId]) {
            nim.sendMsgReceipt({
              msg: this.sessionMap[currSessionId].lastMsg,
              done: function sendMsgReceiptDone() {
                // do something
              }
            })
          }
        }
      }
    },
    // nim初始化同步完成
    onSyncdone() {
      console.log('IM => 同步完成')
      // 取消骨架屏
      this.showSkeleton = false
      // 说明在聊天列表页
      if (this.currSessionId) {
        this.setCurrSession(this.currSessionId)
      }
    },
    // 处理更新用户信息
    updateUserInfo(users) {
      const userInfos = this.userInfos
      if (!Array.isArray(users)) {
        users = [users]
      }
      this.userInfoList = this.nim.mergeUsers(this.userInfoList, users)
      users.forEach((user) => {
        const account = user.account
        if (account) {
          userInfos[account] = user
        }
      })
    },

    // 处理更新会话
    updateSessions(sessions) {
      this.allUnread = 0
      const nim = this.nim
      this.sessionlist = nim.mergeSessions(this.sessionlist, sessions)
      this.sessionlist.sort((a, b) => {
        return b.updateTime - a.updateTime
      })
      this.sessionlist.forEach((item) => {
        this.sessionMap[item.id] = item
        this.allUnread += item.unread
      })
    },
    // 设置当前会话信息
    setCurrSession(sessionId) {
      const nim = this.nim
      if (sessionId) {
        this.updateCurrSessionId({
          type: 'init',
          sessionId
        })
        if (Object.keys(nim).length) {
          // 如果在聊天页面刷新，此时还没有nim实例，需要在onSessions里同步
          nim.setCurrSession(sessionId)
          this.updateCurrSessionMsgs({
            type: 'init',
            sessionId
          })
          // 发送已读回执
          this.sendMsgReceipt()
        }
      }
    },
    // 更新当前会话id，用于唯一判定是否在current session状态
    updateCurrSessionId(obj) {
      const type = obj.type || ''
      if (type === 'destroy') {
        this.currSessionId = ''
        this.finished = false
        this.currSessionMsgs = []
      } else if (type === 'init') {
        if (obj.sessionId && obj.sessionId !== this.currSessionId) {
          this.currSessionId = obj.sessionId
        }
      }
    },
    // 更新当前会话列表的聊天记录，包括历史消息、单聊消息等，不包括聊天室消息
    // replace: 替换idClient的消息
    updateCurrSessionMsgs(obj) {
      const type = obj.type || ''
      if (type === 'destroy') {
        // 清空会话消息
        this.currSessionMsgs = []
        this.currSessionLastMsg = {} as NIMMessage
        this.updateCurrSessionId({
          type: 'destroy'
        })
      } else if (type === 'init') {
        // 初始化会话消息列表
        if (this.currSessionId) {
          const sessionId = this.currSessionId
          // let currSessionMsgs = [].concat(this.msgs[sessionId] || [])
          // // 做消息截断
          // const limit = this.limit
          // const msgLen = currSessionMsgs.length
          // if (msgLen - limit > 0) {
          //   this.currSessionLastMsg = currSessionMsgs[msgLen - limit]
          //   currSessionMsgs = currSessionMsgs.slice(msgLen - limit, msgLen)
          // } else if (msgLen > 0) {
          //   this.currSessionLastMsg = currSessionMsgs[0]
          // } else {
          //   this.currSessionLastMsg = {} as NIMMessage
          // }
          // this.currSessionMsgs = []
          // let lastMsgTime = 0
          // currSessionMsgs.forEach((msg: NIMMessage) => {
          //   if (msg.time - lastMsgTime > 1000 * 60 * 5) {
          //     lastMsgTime = msg.time
          //     this.currSessionMsgs.push({
          //       type: 'timeTag',
          //       text: formatChatDate(msg.time, false),
          //       idClient: getUuiD()
          //     })
          //   }
          //   this.currSessionMsgs.push(msg)
          // })
          // 获取一次历史消息，漫游消息有7天时效，可能会数据量不足导致页面无法上拉加载历史消息
          const scene = common.parseSession(sessionId)?.scene as
            | 'p2p'
            | 'team'
            | 'superTeam'
          const to = common.parseSession(sessionId)?.to as string
          this.nim.getHistoryMsgs({
            scene,
            to: to,
            reverse: false,
            asc: true,
            limit: this.limit,
            done: (_error, obj) => {
              console.log('IM => ', obj.msgs)
              let currSessionMsgs = obj.msgs || []
              // 做消息截断
              const limit = this.limit
              const msgLen = currSessionMsgs.length
              if (msgLen - limit > 0) {
                this.currSessionLastMsg = currSessionMsgs[msgLen - limit]
                currSessionMsgs = currSessionMsgs.slice(msgLen - limit, msgLen)
              } else if (msgLen > 0) {
                this.currSessionLastMsg = currSessionMsgs[0]
              } else {
                this.currSessionLastMsg = {} as NIMMessage
              }
              this.currSessionMsgs = []
              let lastMsgTime = 0
              currSessionMsgs.forEach((msg: NIMMessage) => {
                if (msg.time - lastMsgTime > 1000 * 60 * 5) {
                  lastMsgTime = msg.time
                  this.currSessionMsgs.push({
                    type: 'timeTag',
                    text: common.formatChatDate(msg.time, false),
                    idClient: common.getUuiD()
                  })
                }
                this.currSessionMsgs.push(msg)
              })
            }
          })
        }
      } else if (type === 'put') {
        // 追加一条消息
        const newMsg = obj.msg
        let lastMsgTime = 0
        const lenCurrMsgs = this.currSessionMsgs.length
        if (lenCurrMsgs > 0) {
          lastMsgTime = this.currSessionMsgs[lenCurrMsgs - 1].time ?? 0
        }
        if (newMsg) {
          if (newMsg.time - lastMsgTime > 1000 * 60 * 5) {
            this.currSessionMsgs.push({
              type: 'timeTag',
              text: common.formatChatDate(newMsg.time, false),
              idClient: common.getUuiD()
            })
          }
          this.currSessionMsgs.push(newMsg)
        }
      } else if (type === 'concat') {
        // 一般用于历史消息拼接
        const currSessionMsgs: myMsgType[] = []
        let lastMsgTime = 0
        obj.msgs.forEach((msg) => {
          if (msg.time - lastMsgTime > 1000 * 60 * 5) {
            lastMsgTime = msg.time
            currSessionMsgs.push({
              type: 'timeTag',
              text: common.formatChatDate(msg.time, false),
              idClient: common.getUuiD()
            })
          }
          currSessionMsgs.push(msg)
        })
        currSessionMsgs.reverse()
        currSessionMsgs.forEach((msg) => {
          this.currSessionMsgs.unshift(msg)
        })
        if (obj.msgs[0]) {
          this.currSessionLastMsg = obj.msgs[0]
        }
        emitter.emit('concatMsgsDone')
      } else if (type === 'replace') {
        const msgLen = this.currSessionMsgs.length
        const lastMsgIndex = msgLen - 1
        if (msgLen > 0) {
          for (let i = lastMsgIndex; i >= 0; i--) {
            if (this.currSessionMsgs[i].idClient === obj.idClient) {
              this.currSessionMsgs.splice(i, 1, obj.msg)
              break
            }
          }
        }
      }
    },
    // 销毁当前会话
    resetCurrSession() {
      const nim = this.nim
      nim.resetCurrSession(this.currSessionId)
      this.updateCurrSessionId({
        type: 'destroy'
      })
    },
    // 过滤有效Uids，目前给call完主播发送消息给用户触发埋点使用
    filterValidUids(msgs) {
      const callStore = useCallStore()
      msgs.forEach((v) => {
        // 如果是会话列表v.lastMsg ,如果是单纯信息v
        const lastMsg = v.lastMsg ? v.lastMsg : v
        // 忽略服务端发送的消息和小秘书，过滤有效的用户Uid
        if (
          lastMsg.fromClientType !== 'Server' &&
          lastMsg.from != this.IM_SERVER_ID
        ) {
          // 以下代码是做给 已通话过的主播主动发信息给用户 触发埋点使用;
          // 如果是会话列表来源就要判断一下 unread 是否未读
          if (v.lastMsg && v.unread) {
            callStore.validCallAfterMessageEventLog(
              Number(lastMsg.from),
              lastMsg.time
            )
          } else {
            callStore.validCallAfterMessageEventLog(
              Number(lastMsg.from),
              lastMsg.time
            )
          }
        }
      })
    },
    // 初始化，收到离线漫游消息时调用
    updateMsgs(msgs) {
      const nim = this.nim
      const tempSessionMap = {}
      msgs.forEach((msg) => {
        const sessionId = msg.sessionId
        tempSessionMap[sessionId] = true
        if (!this.msgs[sessionId]) {
          this.msgs[sessionId] = []
        }
        // sdk会做消息去重
        this.msgs[sessionId] = nim.mergeMsgs(this.msgs[sessionId], [msg])
      })
      for (const sessionId in tempSessionMap) {
        this.msgs[sessionId].sort((a, b) => {
          if (a.time === b.time) {
            // 机器人消息，回复消息时间和提问消息时间相同，提问在前，回复在后
            if (a.type === 'robot' && b.type === 'robot') {
              if (a.content && a.content.msgOut) {
                return 1
              }
              if (b.content && b.content.msgOut) {
                return -1
              }
            }
          }
          return a.time - b.time
        })
        if (sessionId === this.currSessionId) {
          this.updateCurrSessionMsgs({
            type: 'init'
          })
        }
      }
    },
    // 更新追加消息，追加一条消息
    putMsg(msg) {
      const sessionId = msg.sessionId
      if (!this.msgs[sessionId]) {
        this.msgs[sessionId] = []
      }
      const tempMsgs = this.msgs[sessionId]
      const lastMsgIndex = tempMsgs.length - 1
      if (tempMsgs.length === 0 || msg.time >= tempMsgs[lastMsgIndex].time) {
        tempMsgs.push(msg)
      } else {
        for (let i = lastMsgIndex; i >= 0; i--) {
          const currMsg = tempMsgs[i]
          if (msg.time >= currMsg.time) {
            this.msgs[sessionId].splice(i, 0, msg)
            break
          }
        }
      }
    },

    // 发送消息
    sendMsg(obj) {
      const nim = this.nim
      obj = obj || {}
      const type = obj.type || ''
      switch (type) {
        case 'text':
          nim.sendText({
            scene: obj.scene,
            to: obj.to,
            text: obj.text,
            done: this.onSendMsgDone,
            needMsgReceipt: obj.needMsgReceipt || false
          })
          break
        case 'gift':
          nim.sendCustomMsg({
            scene: obj.scene,
            to: obj.to,
            content: JSON.stringify(obj.content),
            done: this.onSendMsgDone,
            needMsgReceipt: obj.needMsgReceipt || false
          })
          break
        case 'call':
          nim.sendCustomMsg({
            scene: obj.scene,
            to: obj.to,
            content: JSON.stringify(obj.content),
            done: this.onSendMsgDone,
            needMsgReceipt: obj.needMsgReceipt || false
          })
          break
        default:
          break
      }
    },
    // 发送消息回调
    onSendMsgDone(error, msg) {
      this.onMsg(msg)
    },
    // 获取历史消息
    getHistoryMsgs(obj) {
      const nim = this.nim
      if (Object.keys(nim).length) {
        const { scene, to } = obj
        let options = {
          scene,
          to,
          reverse: false,
          asc: true,
          limit: this.limit,
          done: this.getHistoryMsgsDone
        }
        if (this.currSessionLastMsg) {
          options = Object.assign(options, {
            lastMsgId: this.currSessionLastMsg.idServer,
            endTime: this.currSessionLastMsg.time
          })
        }
        this.nim.getHistoryMsgs(options)
      }
    },
    // 获取历史消息回调
    getHistoryMsgsDone(error, obj) {
      console.log('IM => ', obj.msgs)
      if (obj.msgs) {
        if (obj.msgs.length === 0) {
          this.finished = true
        } else {
          this.updateCurrSessionMsgs({
            type: 'concat',
            msgs: obj.msgs
          })
        }
      }
    },
    // 获取用户资料
    getUser(account) {
      this.nim.getUser &&
        this.nim.getUser({
          account,
          done: (error, user) => {
            if (!error) {
              this.updateUserInfo(user)
            }
          }
        })
    },
    // 获取用户资料数组
    getUsers(accounts) {
      accounts.forEach((value) => {
        this.nim.getUsers &&
          this.nim.getUsers({
            accounts: value,
            done: (error, user) => {
              if (!error) {
                this.updateUserInfo(user)
              }
            }
          })
      })
    },
    // 发送文件消息
    async sendFileMsg(obj) {
      const nim = this.nim
      let { type } = obj
      const { fileInput, localImg } = obj
      if (!type && fileInput) {
        type = 'file'
        if (/\.(png|jpg|bmp|jpeg|gif|webp)$/i.test(fileInput.value)) {
          type = 'image'
        }
      }
      const sendMsgReceipt = this.sendMsgReceipt

      // 自己先插入一条图片类型消息
      const msg = ref({
        time: new Date().getTime(),
        localImg: localImg,
        percentage: 0,
        flow: 'out',
        idClient: common.getUuiD(),
        type
      })
      this.putMsg(msg.value)
      this.updateCurrSessionMsgs({
        type: 'put',
        msg: msg.value
      })

      const data = Object.assign(
        {
          type,
          uploadprogress: function (obj) {
            // console.log('文件总大小: ' + obj.total + 'bytes');
            // console.log('已经上传的大小: ' + obj.loaded + 'bytes');
            // console.log('上传进度: ' + obj.percentage);
            // console.log('上传进度文本: ' + obj.percentageText);
            msg.value.percentage = obj.percentage
          },
          uploaderror: function () {
            fileInput.value = ''
            console && console.log('IM => 上传失败')
          },
          uploaddone: function () {
            fileInput.value = ''
            // console.log(error);
            // console.log(file);
          },
          beforesend: function (msg) {
            console.log('IM => ', msg)
            console.log('IM => 正在发送p2p image消息, id=' + msg.idClient)
          },
          done: () => {
            // 发送已读回执
            sendMsgReceipt()
          }
        },
        obj
      )
      nim.sendFile(data)
    },
    // 更新个人资料
    updateMyInfo(obj: { nick: string; avatar: string }) {
      this.nim.updateMyInfo({
        nick: obj.nick,
        avatar: obj.avatar,
        done: (error, user) => {
          if (!error) {
            this.onMyInfo(user)
          }
        }
      })
    },
    // 断开连接
    disconnect() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.nim.disconnect && this.nim.disconnect()
    },
    // 重置数据
    resetData() {
      this.sessionlist = []
      this.allUnread = 0
      this.translateTexts = {}
      this.userInfos = {}
    }
  },
  persist: {
    key: 'chatStore',
    paths: ['userInfos', 'translateTexts']
  }
})
